/**
 * FontSizeControl — inline A+/A- buttons that adjust a specific font-size
 * preference key (quranArabicFontSize | hadithFontSize | ...).
 *
 * Persists automatically via PreferencesContext (localStorage for guests,
 * backend for logged-in users).
 */
import React from 'react';
import { Type, Plus, Minus } from 'lucide-react';
import { usePreferences, FONT_SIZE_LIMITS } from '../context/PreferencesContext';
import { Button } from './ui/button';

export const FontSizeControl = ({
  prefKey = 'quranArabicFontSize',
  className = '',
  iconSize = 14,
  testid = 'font-size-control',
  showIcon = true,
  ariaLabelDecrease = 'Diminuer la taille du texte',
  ariaLabelIncrease = 'Augmenter la taille du texte',
}) => {
  const { preferences, bumpFontSize } = usePreferences();
  const limits = FONT_SIZE_LIMITS[prefKey];
  if (!limits) return null;
  const current = preferences.textPreferences[prefKey];
  const atMin = current <= limits.min;
  const atMax = current >= limits.max;

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-md border border-border bg-surface-elevated/60 px-1.5 py-0.5 ${className}`}
      data-testid={testid}
    >
      {showIcon && (
        <Type className="text-text-muted flex-shrink-0" style={{ width: iconSize, height: iconSize }} />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 hover:bg-primary/10 disabled:opacity-40"
        disabled={atMin}
        onClick={() => bumpFontSize(prefKey, -1)}
        aria-label={ariaLabelDecrease}
        data-testid={`${testid}-decrease`}
      >
        <Minus style={{ width: iconSize, height: iconSize }} />
      </Button>
      <span
        className="text-[11px] font-mono text-text-muted tabular-nums w-6 text-center select-none"
        data-testid={`${testid}-value`}
      >
        {current}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 hover:bg-primary/10 disabled:opacity-40"
        disabled={atMax}
        onClick={() => bumpFontSize(prefKey, +1)}
        aria-label={ariaLabelIncrease}
        data-testid={`${testid}-increase`}
      >
        <Plus style={{ width: iconSize, height: iconSize }} />
      </Button>
    </div>
  );
};

export default FontSizeControl;
